import {BaseCard} from "../../Components/Cards/BaseCard";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BlackButton} from "../../Components/Buttons/BlackButton";
import {ApplicationCard} from "../../Components/Cards/ApplicationCard";
import {useBoundStore} from "../../Store/store";
import toast from "react-hot-toast";
import {RegularSwitch} from "../../Components/Form/RegularSwitch";
import Wishlist from "../../Assets/images/illustrations/wishlist.svg";
import ApplyToShift from "../../Assets/images/illustrations/apply_to_shift.svg";
import {RemarkForApplying} from "../../Components/Dialogs/ShiftApplications/RemarkForApplying";
import {SelectInput} from "../../Components/Form/SelectInput";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import { MaxHourlyRateWarning } from "../../Components/Dialogs/ShiftApplications/MaxHourlyRateWarning";


export const Shifts = () => {
    const {t} = useTranslation();
    const [alreadyWorked, setAlreadyWorked] = useState(null);

    const [openApplicationPopup, setOpenApplicationPopup] = useState(false);
    const [shiftId, setShiftId] = useState(null);
    const [matchingStatuten, setMatchingStatuten] = useState([]);
    const [openMaxRateWarning, setOpenMaxRateWarning] = useState(false);
    const [pendingApplication, setPendingApplication] = useState(null);
    const [currentFreelancerRate, setCurrentFreelancerRate] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const shifts = useBoundStore((state) => state.shifts);
    const getMyShifts = useBoundStore((state) => state.getMyShifts);
    const getFreelancerHourlyRateApi = useBoundStore((state) => state.getFreelancerHourlyRate);
    const user = useBoundStore((state) => state.user);

    const provinces = [
        {id: 1, value: 'antwerpen', label: 'Antwerpen'},
        {id: 2, value: 'limburg', label: 'Limburg'},
        {id: 3, value: 'oost-vlaanderen', label: 'Oost-Vlaanderen'},
        {id: 4, value: 'vlaams-brabant', label: 'Vlaams-Brabant'},
        {id: 5, value: 'west-vlaanderen', label: 'West-Vlaanderen'},
        {id: 6, value: 'henegouwen', label: 'Henegouwen'},
        {id: 7, value: 'luik', label: 'Luik'},
        {id: 8, value: 'luxemburg', label: 'Luxemburg'},
        {id: 9, value: 'namen', label: 'Namen'},
        {id: 10, value: 'waals-brabant', label: 'Waals-Brabant'},
        {id: 11, value: 'brussels-hoofdstedelijk-gewest', label: 'Brussels Hoofdstedelijk Gewest'}
    ];

    const schema = yup.object({
        provinces: yup.string().nullable(),
        already_worked_for: yup.string().nullable(),
    }).required();

    const {
        getValues,
        setValue,
        control,
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            provinces: [],
            already_worked_for: false,
        }
    });

    useEffect(() => {
        getMyShifts();
    }, [getMyShifts]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            getMyShifts(
                null, null, null, null,
                getValues('provinces')?.length > 0 ? getValues('provinces') : [],
                alreadyWorked ? 1 : null
            );
        }, 60 * 1000);
        return () => clearInterval(interval);
    }, [getMyShifts, getValues, alreadyWorked]);

    const submitShiftApplication = useBoundStore((state) => state.submitShiftApplication);

    const isFreelancerInApplications = (application) => {
        return application?.has_own_application === true;
    }

    const getBranchHourlyRate = (application) => {
        if(['A1','A2'].includes(user?.function)){
            return application?.branch?.max_hourly_rate_vk;
        }

        if(['A0','A3'].includes(user?.function)){
            return application?.branch?.max_hourly_rate_zk;
        }

        return null;
    }

    const computeMatchingStatuten = (application) => {
        const userStatuten = [user?.main_statuut, ...(user?.extra_statuten || [])].filter(Boolean);
        const allowed = application?.allowed_statuten?.length ? application.allowed_statuten : ['freelance'];
        return userStatuten.filter(s => allowed.includes(s));
    };

    const handleApplyClick = async (application) => {
        const branchMaxRate = getBranchHourlyRate(application);
        const freelancerRateObject = await getFreelancerHourlyRateApi(user?.id, application?.branch?.id, true );
        const freelancerRate = freelancerRateObject?.hourly_rate ?? null;
        const isFixedRate = freelancerRateObject?.is_fixed_rate ?? false;
        setCurrentFreelancerRate(freelancerRate);

        const matching = computeMatchingStatuten(application);

        if (matching.includes('freelance') &&
            branchMaxRate != null &&
            freelancerRate != null &&
            branchMaxRate != 0 &&
            !isFixedRate &&
            branchMaxRate < freelancerRate) {
            setPendingApplication(application);
            setMatchingStatuten(matching);
            setOpenMaxRateWarning(true);
        } else {
            setMatchingStatuten(matching);
            setOpenApplicationPopup(true);
            setShiftId(application?.id);
        }
    };

    return (
        <div>
            <BaseCard border={false} className={'mb-4'}>
                <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                    <div>
                        <SelectInput
                            name={'provinces'}
                            options={provinces}
                            setValue={setValue}
                            control={control}
                            value={getValues('provinces') ?? null}
                            placeholder={"selecteer provincies"}
                            translationIndex={'general.fields.provinces'}
                            multiple={true}
                        />
                    </div>
                    <div className={'flex flex-col justify-between items-start'}>
                        <RegularSwitch
                            translationIndex={'shifts.alreadyWorked'}
                            value={alreadyWorked}
                            textAbove={true}
                            setValue={(value) => setAlreadyWorked(value)}
                        />
                    </div>
                    <div className={'flex justify-end items-center'}>
                        <BlackButton onClick={() => {
                            getMyShifts(
                                null,null,null, null,
                                getValues('provinces')?.length > 0 ? getValues('provinces') : [],
                                alreadyWorked ? 1 : null
                            )
                        }}>Search</BlackButton>
                    </div>
                </div>
            </BaseCard>

            <BaseCard border={false}>
                <div className={'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}>
                    {
                        shifts?.length > 0 && shifts?.map((application, index) => {
                            return (
                                <ApplicationCard
                                    key={`shifts_applocations_${index}`}
                                    imageOverride={ApplyToShift}
                                    canApply={!isFreelancerInApplications(application)}
                                    application={application}
                                    onClickButton={() => handleApplyClick(application)}
                                />
                            )
                        })
                    }

                    {
                        shifts?.length === 0 && (
                            <div className={'col-span-4 text-center'}>
                                <img src={Wishlist} alt={'search'} className={'w-1/2 max-h-64 mx-auto my-4'}/>
                                <p className={'text-gray-500'}>{t('shifts.no_shifts_available')}</p>
                            </div>
                        )
                    }

                    <MaxHourlyRateWarning
                        isOpen={openMaxRateWarning}
                        branchRate={getBranchHourlyRate(pendingApplication)}
                        freelancerRate={currentFreelancerRate}
                        onClose={() => {
                            setOpenMaxRateWarning(false);
                            setPendingApplication(null);
                        }}
                        onConfirm={() => {
                            setOpenMaxRateWarning(false);
                            setOpenApplicationPopup(true);
                            setShiftId(pendingApplication?.id);
                            setPendingApplication(null);
                        }}
                    />

                    <RemarkForApplying
                        isOpen={openApplicationPopup}
                        isSubmitting={isSubmitting}
                        matchingStatuten={matchingStatuten}
                        onClose={() => {
                            if (isSubmitting) return;
                            setOpenApplicationPopup(false);
                            setShiftId(null);
                            setIsSubmitting(false);
                        }}
                        onConfirm={async (data) => {
                            if (isSubmitting) return;

                            const currentShiftId = shiftId;

                            if (!currentShiftId) {
                                console.error('No shift ID available');
                                setOpenApplicationPopup(false);
                                return;
                            }

                            setIsSubmitting(true);
                            try {
                                const res = await submitShiftApplication(user?.id, currentShiftId, data.remark, data.applied_as_statuut);

                                if(res === false || res === undefined) {
                                    setIsSubmitting(false);
                                    setOpenApplicationPopup(false);
                                    setShiftId(null);
                                    return;
                                }

                                toast.success(t('applications_module.toast_submitted'));
                                setOpenApplicationPopup(false);
                                setShiftId(null);
                                getMyShifts();
                            } catch (error) {
                                console.error('Error submitting application', error);
                            } finally {
                                setIsSubmitting(false);
                            }
                        }}
                    />
                </div>
            </BaseCard>
        </div>
    )
}
